import { useState, useEffect } from "react";
import { useWorkoutPlanSubscriptions } from "@/hooks/useWorkoutPlanSubscriptions";
import { useWorkoutPlanRoutines } from "@/components/routines/workout-plans/hooks/useWorkoutPlanRoutines";
import { useNavigate } from "react-router-dom";
import { useProfile } from "@/hooks/useProfile";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { PlanHeader } from "./components/PlanHeader";
import { PlanRoutinesList } from "./components/PlanRoutinesList";
import { PlanActions } from "./components/PlanActions";
import { VisibilityConfigDialog } from "../VisibilityConfigDialog";

interface PlanCardProps {
  plan: any;
  getInstructorName: (item: any) => string;
  isPersonalPlan?: boolean;
}

export const PlanCard = ({ plan, getInstructorName, isPersonalPlan }: PlanCardProps) => {
  const { subscribeMutation, unsubscribeMutation } = useWorkoutPlanSubscriptions();
  const { data: routines, isLoading: isLoadingRoutines, error } = useWorkoutPlanRoutines(plan.id);
  const navigate = useNavigate();
  const { profile } = useProfile();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setCurrentUserId(user?.id ?? null);
    });
  }, []);

  const handleSubscribe = () => {
    subscribeMutation.mutate(plan.id);
  };

  const handleUnsubscribe = () => {
    unsubscribeMutation.mutate(plan.id);
  };

  const handleViewPlan = () => {
    navigate(`/rutina/plan/${plan.id}`);
  };

  return (
    <div className={`bg-card backdrop-blur-lg rounded-xl p-4 border transition-all hover:shadow-md ${
      isPersonalPlan 
        ? 'border-primary border-2' 
        : 'border-border hover:border-primary'
    }`}>
      <PlanHeader
        name={plan.name}
        description={plan.description}
        instructorName={getInstructorName(plan)}
        isPersonalPlan={isPersonalPlan}
      />
      
      <Dialog>
        <PlanActions
          isPersonalPlan={isPersonalPlan}
          isSubscribed={plan.isSubscribed}
          planId={plan.id}
          subscribeMutation={{
            ...subscribeMutation,
            mutate: handleSubscribe
          }}
          unsubscribeMutation={{
            ...unsubscribeMutation,
            mutate: handleUnsubscribe
          }}
          DialogTrigger={DialogTrigger}
          onViewPlan={handleViewPlan}
        />
        <DialogContent className="bg-background/95 backdrop-blur-md text-foreground border-border max-w-2xl w-[90vw]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-primary">
              {plan.name}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {plan.description}
            </DialogDescription>
          </DialogHeader>
          <PlanRoutinesList
            routines={routines || []}
            isLoadingRoutines={isLoadingRoutines}
            error={error}
            isPersonalPlan={isPersonalPlan}
          />
        </DialogContent>
      </Dialog>
      
      {plan.created_by === currentUserId && profile?.gym_id && (
        <div className="mt-2">
          <VisibilityConfigDialog
            contentType="workout_plan"
            contentId={plan.id}
            gymId={profile.gym_id}
          />
        </div>
      )}
    </div>
  );
};